/**
 * HairlineDivider — macOS 특유의 0.5px 구분선
 *
 * macOS UI 는 일반 1px border 대신 0.5px (Retina 1 device pixel) 을 사용해
 * "얇고 섬세한" 경계선을 만든다. Safari/Chrome 모두 0.5px 를 지원.
 *
 * 용도:
 *  - 사이드바 섹션 구분
 *  - 카드 내부 row 구분
 *  - 툴바 · 상단 타이틀바 구분
 *
 * 접근성:
 *  - `role="separator"` + `aria-orientation` 명시
 *  - 단순 시각적 구분만 필요하면 `decorative` 플래그로 role 제거
 */

import type { HTMLAttributes } from 'react';
import { cn } from '../utils/cn';

type Orientation = 'horizontal' | 'vertical';

interface HairlineDividerProps extends HTMLAttributes<HTMLDivElement> {
  /** 기본 horizontal */
  orientation?: Orientation;
  /** 순수 장식용이면 true — role 제거 (스크린리더 무시) */
  decorative?: boolean;
  /** 양쪽 여백 (Tailwind margin 클래스로 override 가능) */
  spacing?: 'none' | 'sm' | 'md' | 'lg';
}

const SPACING_H: Record<NonNullable<HairlineDividerProps['spacing']>, string> = {
  none: 'my-0',
  sm: 'my-1',
  md: 'my-2',
  lg: 'my-4',
};

const SPACING_V: Record<NonNullable<HairlineDividerProps['spacing']>, string> = {
  none: 'mx-0',
  sm: 'mx-1',
  md: 'mx-2',
  lg: 'mx-4',
};

/** 0.5px 구분선 — light/dark 자동 */
const BASE_BG = 'bg-[rgba(0,0,0,0.08)] dark:bg-[rgba(255,255,255,0.12)]';

export function HairlineDivider({
  orientation = 'horizontal',
  decorative = false,
  spacing = 'none',
  className,
  ...rest
}: HairlineDividerProps) {
  const isVertical = orientation === 'vertical';

  return (
    <div
      role={decorative ? undefined : 'separator'}
      aria-orientation={decorative ? undefined : orientation}
      aria-hidden={decorative ? true : undefined}
      className={cn(
        BASE_BG,
        isVertical
          ? ['w-[0.5px]', 'self-stretch', SPACING_V[spacing]]
          : ['h-[0.5px]', 'w-full', SPACING_H[spacing]],
        className,
      )}
      {...rest}
    />
  );
}

HairlineDivider.displayName = 'HairlineDivider';
